import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import type { Member, MemberLocation, MemberStatus } from '@pacer/shared';
import * as api from '../api';
import { FamilyMap } from './FamilyMap';
import { Icon, type IconName } from './Icon';
import { Modal } from './Modal';
import { Avatar, Button, Field, IconButton, Input, cx } from './ui';
import { fmtTime, useZone } from '../lib/dates';
import { getMyLocation } from '../state/geo';
import { queryKeys } from '../state/queries';
import { useActiveMemberId } from '../state/activeMember';
import { setLocationSharing, useLocationSharingEnabled } from '../state/shareLocation';

const PRESETS: Array<{ label: string; icon: IconName }> = [
  { label: 'Golf', icon: 'golf' },
  { label: 'Gym', icon: 'activity' },
  { label: 'Work', icon: 'briefcase' },
  { label: 'Errands', icon: 'cart' },
  { label: 'Out', icon: 'map' },
];

function iconFor(activity: string): IconName {
  const p = PRESETS.find((x) => x.label.toLowerCase() === activity.trim().toLowerCase());
  return p?.icon ?? 'map';
}

/**
 * "Out & about" — who in the family is out, doing what, with whom, and when they're back.
 * The glanceable answer to "where's Dad?" on the kitchen wall.
 */
export function StatusCard() {
  const zone = useZone();
  const activeId = useActiveMemberId();
  const sharing = useLocationSharingEnabled();
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [mapFor, setMapFor] = useState<string | 'all' | null>(null);
  const { data: statuses = [] } = useQuery({ queryKey: queryKeys.statuses, queryFn: api.getStatuses });
  const { data: members = [] } = useQuery({ queryKey: queryKeys.members, queryFn: api.getMembers });
  const { data: locations = [] } = useQuery({ queryKey: queryKeys.locations, queryFn: api.getLocations });

  const withMember = statuses
    .map((s) => ({ status: s, member: members.find((m) => m.id === s.memberId) }))
    .filter((x): x is { status: MemberStatus; member: Member } => Boolean(x.member));
  const located = members
    .map((m) => ({ member: m, loc: locations.find((l) => l.memberId === m.id) }))
    .filter((x): x is { member: Member; loc: MemberLocation } => Boolean(x.loc));
  const hasLocations = locations.length > 0;
  const me = members.find((m) => m.id === activeId);

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center gap-2 border-b border-border-subtle pb-3">
        <Icon name="map" size={16} className="text-muted" />
        <h2 className="font-display text-[20px] text-primary">Out &amp; about</h2>
        {hasLocations && (
          <IconButton icon="map" label="Family map" size="sm" className="ml-auto" onClick={() => setMapFor('all')} />
        )}
        <IconButton
          icon="plus"
          label="Update status"
          size="sm"
          className={hasLocations ? '' : 'ml-auto'}
          onClick={() => setEditing('new')}
        />
      </div>

      {withMember.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border-subtle px-4 py-5 text-center font-mono text-[12px] text-muted">
          Nobody's out right now.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {withMember.map(({ status, member }) => (
            <button
              key={member.id}
              onClick={() => setEditing(member.id)}
              className="group flex items-start gap-3 rounded-lg border border-border-subtle border-l-2 bg-surface px-3 py-2.5 text-left transition-colors hover:border-border hover:bg-surface-high/60"
              style={{ borderLeftColor: member.color }}
            >
              <Avatar name={member.name} color={member.color} photoUrl={member.avatarUrl} size={34} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <Icon name={iconFor(status.activity)} size={13} className="shrink-0 text-secondary" />
                  <span className="truncate text-[14px] text-primary">
                    {member.name} · {status.activity}
                    {status.place && <span className="text-secondary"> at {status.place}</span>}
                  </span>
                </div>
                <div className="mt-0.5 flex flex-wrap gap-x-3 font-mono text-[12px] text-muted">
                  {status.companions && <span>with {status.companions}</span>}
                  {status.startsAt && <span>tee {fmtTime(status.startsAt, zone)}</span>}
                  {status.etaBack && <span>back ~{fmtTime(status.etaBack, zone)}</span>}
                  {locations.some((l) => l.memberId === member.id) && (
                    <span
                      role="link"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        setMapFor(member.id);
                      }}
                      className="flex cursor-pointer items-center gap-1 text-accent hover:underline"
                    >
                      <Icon name="pin" size={11} /> on the map
                    </span>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {located.length > 0 && (
        <div className="flex flex-col gap-1 border-t border-border-subtle pt-3">
          <span className="eyebrow">Where everyone is</span>
          {located.map(({ member, loc }) => (
            <button
              key={member.id}
              onClick={() => setMapFor(member.id)}
              className="flex items-center gap-2.5 rounded-md px-1 py-1 text-left transition-colors hover:bg-surface-high/50"
            >
              <Avatar name={member.name} color={member.color} photoUrl={member.avatarUrl} size={26} />
              <span className="min-w-0 flex-1 truncate text-[13px] text-primary">
                {member.name}
                {loc.label && <span className="text-secondary"> · {loc.label}</span>}
              </span>
              <span className="tabular shrink-0 font-mono text-[12px] text-muted">
                {DateTime.fromISO(loc.updatedAt).toRelative({ style: 'short' })}
              </span>
              <Icon name="pin" size={12} className="shrink-0 text-accent" />
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 border-t border-border-subtle pt-3">
        <span className="min-w-0 flex-1 font-mono text-[12px] text-muted">
          {me ? `Update ${me.name}'s pin while Pacer is open` : 'Tap your avatar to share your location'}
        </span>
        <Button
          variant={sharing ? 'primary' : 'secondary'}
          size="sm"
          icon={sharing ? 'check' : 'pin'}
          disabled={!me}
          onClick={() => setLocationSharing(!sharing)}
        >
          {sharing ? 'Sharing' : 'Share mine'}
        </Button>
      </div>

      {editing && (
        <StatusEditor
          members={members}
          memberId={editing === 'new' ? null : editing}
          existing={editing === 'new' ? undefined : statuses.find((s) => s.memberId === editing)}
          onClose={() => setEditing(null)}
        />
      )}
      {mapFor && (
        <FamilyMap focusMemberId={mapFor === 'all' ? undefined : mapFor} onClose={() => setMapFor(null)} />
      )}
    </section>
  );
}

function StatusEditor({
  members,
  memberId,
  existing,
  onClose,
}: {
  members: Member[];
  memberId: string | null;
  existing?: MemberStatus;
  onClose: () => void;
}) {
  const zone = useZone();
  const qc = useQueryClient();
  const activeId = useActiveMemberId();

  const [who, setWho] = useState(memberId ?? existing?.memberId ?? activeId ?? members[0]?.id ?? '');
  const [activity, setActivity] = useState(existing?.activity ?? '');
  const [place, setPlace] = useState(existing?.place ?? '');
  const [companions, setCompanions] = useState(existing?.companions ?? '');
  const [tee, setTee] = useState(existing?.startsAt ? DateTime.fromISO(existing.startsAt, { zone }).toFormat('HH:mm') : '');
  const [back, setBack] = useState(existing?.etaBack ? DateTime.fromISO(existing.etaBack, { zone }).toFormat('HH:mm') : '');
  const [locShared, setLocShared] = useState(false);
  const [locBusy, setLocBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A bare HH:mm is anchored to today in the household zone, then sent as a UTC instant.
  const toIso = (t: string): string | null => {
    if (!t) return null;
    const [h, m] = t.split(':').map(Number);
    return DateTime.now().setZone(zone).startOf('day').plus({ hours: h ?? 0, minutes: m ?? 0 }).toUTC().toISO();
  };

  const refresh = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: queryKeys.statuses });
    onClose();
  };

  const save = useMutation({
    mutationFn: () =>
      api.setStatus(who, {
        activity: activity.trim(),
        place: place.trim() || null,
        companions: companions.trim() || null,
        startsAt: toIso(tee),
        etaBack: toIso(back),
      }),
    onSuccess: refresh,
    onError: (e: Error) => setError(e.message),
  });

  const clear = useMutation({ mutationFn: () => api.clearStatus(who), onSuccess: refresh });

  // Location is stored separately (member_location) so it can also come from a phone
  // automation — capturing it here writes straight through, independent of Save.
  const captureLocation = async (): Promise<void> => {
    setLocBusy(true);
    setError(null);
    try {
      const { lat, lon } = await getMyLocation();
      await api.setLocation(who, lat, lon);
      await qc.invalidateQueries({ queryKey: queryKeys.locations });
      setLocShared(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'location failed');
    } finally {
      setLocBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      eyebrow="Where I am"
      title={existing ? 'Update status' : 'Set a status'}
      footer={
        <>
          {existing && (
            <Button variant="danger" onClick={() => clear.mutate()} disabled={clear.isPending}>
              I'm home
            </Button>
          )}
          <Button
            variant="primary"
            className="ml-auto min-w-24"
            onClick={() => save.mutate()}
            disabled={!who || !activity.trim() || save.isPending}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {members.length > 1 && !existing && (
          <Field label="Who">
            <div className="flex flex-wrap gap-2 pt-1">
              {members.map((m) => (
                <button
                  key={m.id}
                  onClick={() => setWho(m.id)}
                  className="flex items-center gap-2 rounded-full border py-1 pr-3 pl-1 transition-colors"
                  style={{
                    borderColor: who === m.id ? m.color : 'var(--color-border)',
                    background: who === m.id ? `color-mix(in srgb, ${m.color} 12%, transparent)` : 'transparent',
                  }}
                >
                  <Avatar name={m.name} color={m.color} photoUrl={m.avatarUrl} size={24} />
                  <span className="text-[13px]" style={{ color: who === m.id ? m.color : 'var(--color-text-secondary)' }}>
                    {m.name}
                  </span>
                </button>
              ))}
            </div>
          </Field>
        )}

        <Field label="What">
          <div className="mb-2 flex flex-wrap gap-2">
            {PRESETS.map((pr) => (
              <button
                key={pr.label}
                onClick={() => setActivity(pr.label)}
                className={cx(
                  'flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[13px] transition-colors',
                  activity.toLowerCase() === pr.label.toLowerCase()
                    ? 'border-accent text-accent'
                    : 'border-border-subtle text-secondary hover:text-primary',
                )}
              >
                <Icon name={pr.icon} size={13} />
                {pr.label}
              </button>
            ))}
          </div>
          <Input value={activity} onChange={(e) => setActivity(e.target.value)} placeholder="Golf" autoCapitalize="sentences" />
        </Field>

        <Field label="Where">
          <Input value={place} onChange={(e) => setPlace(e.target.value)} placeholder="Pebble Beach" autoCapitalize="words" />
        </Field>

        <Field label="With">
          <Input value={companions} onChange={(e) => setCompanions(e.target.value)} placeholder="the Johnsons" autoCapitalize="words" />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Tee time">
            <Input type="time" value={tee} onChange={(e) => setTee(e.target.value)} />
          </Field>
          <Field label="Home by">
            <Input type="time" value={back} onChange={(e) => setBack(e.target.value)} />
          </Field>
        </div>

        <Field label="Location" hint="A one-time pin from this device. For automatic arrive/leave, see a person's settings.">
          <Button variant="secondary" icon={locShared ? 'check' : 'pin'} onClick={captureLocation} disabled={locBusy || !who}>
            {locBusy ? 'Locating…' : locShared ? 'Location shared' : 'Share my location'}
          </Button>
        </Field>

        {error && <p className="font-mono text-[12px] text-danger">{error}</p>}
      </div>
    </Modal>
  );
}
